import { add, format, isAfter, parse } from 'date-fns'
import { useFormikContext, Formik, Form, FormikOnSubmit } from 'formik'
import {
  Button,
  Box,
  Datepicker,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalProps,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@ritualco/jutsu'
import {
  FormikCurrencyInput,
  FormikDatepicker,
  FormikDayPicker,
  FormikDayRangePicker,
  FormikInput,
  FormikSelect,
  FormikTextarea,
  FormikTimeSelect,
} from 'components/Formik'
import { HeraModal } from 'components/HeraModal'
import { QuestionTooltip } from 'components/QuestionTooltip'
import { useContent } from 'lib/content/react'
import { selectUserInfo } from 'state/selectors/userInfo'
import { useStore } from 'state/store'
import { IncentiveDetails } from 'types/entities'
import { useIncentiveDetailsModal, IncentiveDetailsSchema } from './helpers'
import {
  getDateParts,
  CREDIT_TYPES,
  RECURRING_SCHEDULE_TYPE_OPTIONS,
  SCHEDULE_FREQUENCY_OPTIONS,
  SCHEDULE_TYPE_OPTIONS,
  TIME_FORMAT,
  WEEK_RANGE_OPTIONS,
} from './utils'

const IncentiveDetailsForm = () => {
  const { formatMessage } = useContent()
  const { locale } = useStore(selectUserInfo)
  const { values, setFieldValue } = useFormikContext<IncentiveDetailsSchema>()
  const { schedule } = values
  if (!schedule) return null // this should never happen
  const { monthDay, weekDayName, weekDayOrdinal } = getDateParts(
    locale || 'en-US',
    schedule.start
  )

  return (
    <Form id="incentiveDetails" noValidate>
      <VStack spacing={6}>
        <FormikInput
          name="name"
          label={formatMessage({ id: 'pmportal.incentives.nameLabel' })}
        />
        <FormikTextarea
          name="description"
          label={formatMessage({
            id: 'pmportal.incentives.descriptionLabel',
          })}
          placeholder={formatMessage({
            id: 'pmportal.incentives.descriptionPlaceholder',
          })}
        />
      </VStack>
      <Text fontSize="large" fontWeight="medium" mt={7} mb={5}>
        {formatMessage({ id: 'pmportal.incentives.whatWillYouOffer' })}
      </Text>
      <VStack spacing={6}>
        <FormikSelect
          name="type"
          label={
            <QuestionTooltip
              label={formatMessage({
                id: 'pmportal.incentives.incentiveTypeTooltip',
              })}
            >
              {formatMessage({
                id: 'pmportal.incentives.incentiveTypeLabel',
              })}
            </QuestionTooltip>
          }
          onChange={e => {
            const newValue = e.target.value
            setFieldValue('type', newValue)
            if (newValue === 'creditRecurring' && schedule.type === 'none') {
              setFieldValue('schedule.type', 'daily')
            }
          }}
        >
          <option value="initial" hidden>
            {formatMessage({ id: 'pmportal.incentives.selectAType' })}
          </option>
          <option value="creditOneTime">
            {formatMessage({ id: 'pmportal.incentives.creditOneTimeType' })}
          </option>
          <option value="creditRecurring">
            {formatMessage({ id: 'pmportal.incentives.creditRecurringType' })}
          </option>
        </FormikSelect>
        {values.type && CREDIT_TYPES.includes(values.type) && (
          <FormikCurrencyInput
            name="creditAmount"
            label={formatMessage({
              id: 'pmportal.incentives.creditAmountLabel',
            })}
            placeholder="0.00"
          />
        )}
      </VStack>
      <Text fontSize="large" fontWeight="medium" mt={7} mb={5}>
        {formatMessage({ id: 'pmportal.incentives.whenIsItAvailable' })}
      </Text>
      {values.type === 'creditOneTime' && (
        <HStack spacing={6}>
          <FormikDatepicker
            name="schedule.start"
            label={formatMessage({
              id: 'pmportal.incentives.scheduleStartOneTimeLabel',
            })}
            minDate={new Date()}
          />
          <Box w="100%">
            <FormLabel>
              {formatMessage({ id: 'pmportal.incentives.oneYearExpiration' })}
            </FormLabel>
            <Datepicker disabled selected={add(schedule.start, { years: 1 })} />
          </Box>
        </HStack>
      )}
      {values.type !== 'creditOneTime' && (
        <VStack spacing={6}>
          <FormikDatepicker
            name="schedule.start"
            label={formatMessage({
              id: 'pmportal.incentives.scheduleStartLabel',
            })}
            minDate={new Date()}
          />
          <FormikSelect
            name="schedule.type"
            label={formatMessage({
              id: 'pmportal.incentives.scheduleTypeLabel',
            })}
          >
            {values.type === 'creditRecurring'
              ? RECURRING_SCHEDULE_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {formatMessage({ id: label })}
                  </option>
                ))
              : SCHEDULE_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {formatMessage({ id: label })}
                  </option>
                ))}
          </FormikSelect>
          {schedule.type === 'daily' && (
            <FormikDayPicker
              name="schedule.days"
              label={formatMessage({
                id: 'pmportal.incentives.scheduleDaysLabel',
              })}
            />
          )}
          {schedule.type === 'weekly' && (
            <HStack spacing={6} w="100%">
              <FormikDayRangePicker
                name="schedule.weekStart"
                label={formatMessage({
                  id: 'pmportal.incentives.scheduleWeekStartLabel',
                })}
                range={schedule.weekRange}
              />
              <FormikSelect
                name="schedule.weekRange"
                label={formatMessage({
                  id: 'pmportal.incentives.scheduleWeekRangeLabel',
                })}
                onChange={e =>
                  setFieldValue(
                    'schedule.weekRange',
                    parseInt(e.target.value, 10)
                  )
                }
              >
                {WEEK_RANGE_OPTIONS.map(value => (
                  <option key={value} value={value}>
                    {formatMessage(
                      { id: 'pmportal.incentives.days' },
                      { count: value }
                    )}
                  </option>
                ))}
              </FormikSelect>
            </HStack>
          )}
          {schedule.type === 'monthly' && (
            <FormikSelect
              name="schedule.monthlyOccurrence"
              label={formatMessage({
                id: 'pmportal.incentives.monthlyOccurrenceLabel',
              })}
            >
              <option value="first">
                {formatMessage(
                  { id: 'pmportal.incentives.firstWeekDayMonthOption' },
                  { name: weekDayName }
                )}
              </option>
              <option value="second">
                {formatMessage(
                  { id: 'pmportal.incentives.secondWeekDayMonthOption' },
                  { name: weekDayName }
                )}
              </option>
              <option value="third">
                {formatMessage(
                  { id: 'pmportal.incentives.thirdWeekDayMonthOption' },
                  { name: weekDayName }
                )}
              </option>
              <option value="fourth">
                {formatMessage(
                  { id: 'pmportal.incentives.fourthWeekDayMonthOption' },
                  { name: weekDayName }
                )}
              </option>
              <option value="last">
                {formatMessage(
                  { id: 'pmportal.incentives.lastWeekDayMonthOption' },
                  { name: weekDayName }
                )}
              </option>
            </FormikSelect>
          )}
          {schedule.type === 'custom' && (
            <>
              <HStack w="100%" alignItems="flex-start">
                <FormikInput
                  name="schedule.interval"
                  label={formatMessage({
                    id: 'pmportal.incentives.scheduleIntervalLabel',
                  })}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <FormikSelect name="schedule.customFrequency" mt={7}>
                  {SCHEDULE_FREQUENCY_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {formatMessage({ id: label })}
                    </option>
                  ))}
                </FormikSelect>
              </HStack>
              {schedule.customFrequency === 'weekly' && (
                <HStack spacing={6} w="100%">
                  <FormikDayRangePicker
                    name="schedule.weekStart"
                    label={formatMessage({
                      id: 'pmportal.incentives.scheduleWeekStartLabel',
                    })}
                    range={schedule.weekRange}
                  />
                  <FormikSelect
                    name="schedule.weekRange"
                    label={formatMessage({
                      id: 'pmportal.incentives.scheduleWeekRangeLabel',
                    })}
                    onChange={e =>
                      setFieldValue(
                        'schedule.weekRange',
                        parseInt(e.target.value, 10)
                      )
                    }
                  >
                    {WEEK_RANGE_OPTIONS.map(value => (
                      <option key={value} value={value}>
                        {formatMessage(
                          { id: 'pmportal.incentives.days' },
                          { count: value }
                        )}
                      </option>
                    ))}
                  </FormikSelect>
                </HStack>
              )}
              {schedule.customFrequency === 'monthly' && (
                <FormikSelect
                  name="customOccurrence"
                  label={formatMessage({
                    id: 'pmportal.incentives.customOccurrenceLabel',
                  })}
                >
                  <option value="SAME_MONTH_DAY">
                    {formatMessage(
                      {
                        id: 'pmportal.incentives.monthlyOrdinalMonthDayOption',
                      },
                      { number: monthDay }
                    )}
                  </option>
                  <option value="SAME_WEEK_DAY">
                    {formatMessage(
                      {
                        id: 'pmportal.incentives.monthlyOrdinalWeekDayOption',
                      },
                      { number: weekDayOrdinal, name: weekDayName }
                    )}
                  </option>
                </FormikSelect>
              )}
            </>
          )}
          <HStack alignSelf="flex-start" alignItems="stretch">
            <FormikTimeSelect
              name="schedule.fromTime"
              label={formatMessage({
                id: 'pmportal.incentives.fromTimeLabel',
              })}
              lastTime="23:00"
              onChange={timeString => {
                setFieldValue('schedule.fromTime', timeString)
                const fromTime = parse(timeString, TIME_FORMAT, new Date(0))
                const toTime = parse(schedule.toTime, TIME_FORMAT, new Date(0))
                if (isAfter(fromTime, toTime)) {
                  const newToTime = format(
                    add(fromTime, { minutes: 30 }),
                    TIME_FORMAT
                  )
                  setFieldValue('schedule.toTime', newToTime)
                }
              }}
            />
            <Flex
              fontWeight="medium"
              color="grayscale.secondary"
              alignItems="center"
            >
              <Box mt={7}>-</Box>
            </Flex>
            <FormikTimeSelect
              name="schedule.toTime"
              label={formatMessage({ id: 'pmportal.incentives.toTimeLabel' })}
              firstTime={schedule.fromTime}
            />
          </HStack>
          {schedule.type !== 'none' && (
            <FormControl id="schedule.ending">
              <FormLabel mb={2}>
                {formatMessage({
                  id: 'pmportal.incentives.scheduleEndingLabel',
                })}
              </FormLabel>
              <RadioGroup
                value={schedule.ending}
                onChange={value => {
                  setFieldValue('schedule.ending', value)
                }}
              >
                <VStack spacing={4} alignItems="unset">
                  <Radio value="never" size="lg">
                    {formatMessage({
                      id: 'pmportal.incentives.endingNeverOption',
                    })}
                  </Radio>
                  <Flex justifyContent="space-between" w="100%'">
                    <Radio value="date" size="lg">
                      {formatMessage({
                        id: 'pmportal.incentives.endingDateOption',
                      })}
                    </Radio>
                    <Datepicker
                      disabled={schedule.ending !== 'date'}
                      minDate={schedule.start}
                      selected={schedule.endingOn}
                      onChange={(date: Date) => {
                        setFieldValue('schedule.endingOn', date)
                      }}
                    />
                  </Flex>
                </VStack>
              </RadioGroup>
            </FormControl>
          )}
        </VStack>
      )}
    </Form>
  )
}

export type IncentiveDetailsModalOnSubmit = FormikOnSubmit<IncentiveDetailsSchema>

export type IncentiveDetailsModalProps = {
  incentiveDetails?: IncentiveDetails
  defaultCurrencyCode: string
  defaultTimezone: string
  onSubmit: IncentiveDetailsModalOnSubmit
} & Omit<ModalProps, 'children'>

export const IncentiveDetailsModal = ({
  incentiveDetails = {},
  defaultCurrencyCode,
  defaultTimezone,
  onSubmit,
  onClose,
  ...modalProps
}: IncentiveDetailsModalProps) => {
  const { formatMessage } = useContent()
  const { initialValues, schema } = useIncentiveDetailsModal(
    incentiveDetails,
    defaultCurrencyCode,
    defaultTimezone
  )
  return (
    <HeraModal size="2xl" onClose={onClose} {...modalProps}>
      <Formik<IncentiveDetailsSchema>
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values, actions) => {
          await onSubmit(values, actions)
          actions.setSubmitting(false)
          onClose()
        }}
      >
        {props => (
          <>
            <ModalHeader>
              {formatMessage({ id: 'pmportal.incentives.detailsModalTitle' })}
            </ModalHeader>
            <ModalBody>
              <IncentiveDetailsForm />
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="transparent"
                variant="ghost"
                mr={2}
                onClick={onClose}
              >
                {formatMessage({ id: 'pmportal.global.actionCancel' })}
              </Button>
              <Button
                type="submit"
                form="incentiveDetails"
                isLoading={props.isSubmitting}
              >
                {formatMessage({ id: 'pmportal.global.actionSave' })}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </HeraModal>
  )
}
